/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ManagedDeviceAgentState,
  ManagedDeviceAgentState$inboundSchema,
} from "./manageddeviceagentstate.js";

/**
 * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
 */
export const CoverageBucket = {
  AgentActive: "agent_active",
  AgentStale: "agent_stale",
  AgentOtherDevice: "agent_other_device",
  NoAgent: "no_agent",
  NoEmail: "no_email",
  UnresolvedEmail: "unresolved_email",
  Missing: "missing",
} as const;
/**
 * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
 */
export type CoverageBucket = OpenEnum<typeof CoverageBucket>;

/**
 * Whether the agent that answers for an agent_active device verifiably applies its policy; agent_state.attestation says whether that agent runs on this machine. attested: its latest report applies the policy last served to the reporting user. outdated: it reports a different policy, or none. unreported: no report within the window, from an agent that predates reporting or has stopped sending. halted: the reporting agent's kill switch stopped enforcement on a blocked build. Omitted for every other bucket.
 */
export const PolicyState = {
  Attested: "attested",
  Outdated: "outdated",
  Unreported: "unreported",
  Halted: "halted",
} as const;
/**
 * Whether the agent that answers for an agent_active device verifiably applies its policy; agent_state.attestation says whether that agent runs on this machine. attested: its latest report applies the policy last served to the reporting user. outdated: it reports a different policy, or none. unreported: no report within the window, from an agent that predates reporting or has stopped sending. halted: the reporting agent's kill switch stopped enforcement on a blocked build. Omitted for every other bucket.
 */
export type PolicyState = OpenEnum<typeof PolicyState>;

/**
 * One device the organization can see: a device from a connected MDM's inventory, or a machine running the Speakeasy device agent that no connected inventory holds. MDM devices carry the same coverage bucket as listManagedDevices; agent-only devices are only ever agent_active or agent_stale, classified by their own heartbeat and state reports.
 */
export type FleetDevice = {
  /**
   * The latest sign of life that classified this device, a policy poll or a state report. Omitted when no agent has ever synced.
   */
  agentLastSeenAt?: Date | undefined;
  /**
   * What a device's agent last reported it enforces. Carries no error text or user-facing message.
   */
  agentState?: ManagedDeviceAgentState | undefined;
  /**
   * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
   */
  coverageBucket: CoverageBucket;
  /**
   * The MDM's identifier for the device. Omitted for an agent-only machine.
   */
  externalId?: string | undefined;
  /**
   * When the MDM or the agent first reported this device.
   */
  firstSeenAt: Date;
  /**
   * Device hostname, from the MDM or the agent.
   */
  hostname?: string | undefined;
  /**
   * Stable device ID: mdm:<uuid> for an MDM device, agent:<uuid> for an agent-only machine.
   */
  id: string;
  /**
   * True when a connected MDM's inventory holds this device. False for a machine only its agent reports.
   */
  inInventory: boolean;
  /**
   * When the MDM or the agent last reported this device.
   */
  lastSeenAt: Date;
  /**
   * Last device check-in as reported by the MDM.
   */
  mdmLastCheckInAt?: Date | undefined;
  /**
   * When the device went absent from the MDM inventory. Omitted while present, and for an agent-only machine.
   */
  missingSince?: Date | undefined;
  /**
   * Operating system name as the MDM reports it. Omitted for an agent-only machine; agent_state.os carries the agent's.
   */
  osName?: string | undefined;
  /**
   * Operating system version as the MDM reports it. Omitted for an agent-only machine.
   */
  osVersion?: string | undefined;
  /**
   * Whether the agent that answers for an agent_active device verifiably applies its policy; agent_state.attestation says whether that agent runs on this machine. attested: its latest report applies the policy last served to the reporting user. outdated: it reports a different policy, or none. unreported: no report within the window, from an agent that predates reporting or has stopped sending. halted: the reporting agent's kill switch stopped enforcement on a blocked build. Omitted for every other bucket.
   */
  policyState?: PolicyState | undefined;
  /**
   * Provider the device was synced from. Omitted for an agent-only machine.
   */
  provider?: string | undefined;
  /**
   * Hardware serial number, from the MDM or the agent. Omitted when neither reported one.
   */
  serialNumber?: string | undefined;
  /**
   * The MDM's assigned user, or for an agent-only machine the user its agent runs as.
   */
  userEmail?: string | undefined;
  /**
   * Resolved Speakeasy user for user_email. Omitted when it does not resolve to an org member.
   */
  userId?: string | undefined;
};

/** @internal */
export const CoverageBucket$inboundSchema: z.ZodMiniType<
  CoverageBucket,
  unknown
> = openEnums.inboundSchema(CoverageBucket);

/** @internal */
export const PolicyState$inboundSchema: z.ZodMiniType<PolicyState, unknown> =
  openEnums.inboundSchema(PolicyState);

/** @internal */
export const FleetDevice$inboundSchema: z.ZodMiniType<FleetDevice, unknown> = z
  .pipe(
    z.object({
      agent_last_seen_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      agent_state: z.optional(ManagedDeviceAgentState$inboundSchema),
      coverage_bucket: CoverageBucket$inboundSchema,
      external_id: z.optional(z.string()),
      first_seen_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      hostname: z.optional(z.string()),
      id: z.string(),
      in_inventory: z.boolean(),
      last_seen_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      mdm_last_check_in_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      missing_since: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      os_name: z.optional(z.string()),
      os_version: z.optional(z.string()),
      policy_state: z.optional(PolicyState$inboundSchema),
      provider: z.optional(z.string()),
      serial_number: z.optional(z.string()),
      user_email: z.optional(z.string()),
      user_id: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "agent_last_seen_at": "agentLastSeenAt",
        "agent_state": "agentState",
        "coverage_bucket": "coverageBucket",
        "external_id": "externalId",
        "first_seen_at": "firstSeenAt",
        "in_inventory": "inInventory",
        "last_seen_at": "lastSeenAt",
        "mdm_last_check_in_at": "mdmLastCheckInAt",
        "missing_since": "missingSince",
        "os_name": "osName",
        "os_version": "osVersion",
        "policy_state": "policyState",
        "serial_number": "serialNumber",
        "user_email": "userEmail",
        "user_id": "userId",
      });
    }),
  );

export function fleetDeviceFromJSON(
  jsonString: string,
): SafeParseResult<FleetDevice, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => FleetDevice$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'FleetDevice' from JSON`,
  );
}
